/**
 * AddWishProductForm — full mobile-first "Add Wish Product" form
 * matching the user's screenshots.
 *
 * Route: /food-pulse/lists/wishlist/new
 * Optional prefill via location.state.product (from FoodItemPage) or
 * ?slug=... (from anywhere else). The top of the page always exposes a
 * "Scan a product" button that opens ProductScannerModal — a matched
 * scan prefills the form and drops the user at the "Vendor / Buying
 * For / Notes" section.
 *
 * On submit, POST /api/lists/wishlist  — the backend auto-mirrors into
 * fp_shopping / fp_pantry / fp_donation based on the toggles here.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Camera, Plus, Trash2, X, Package, Layers,
  ChevronDown, Users, Sparkles, Store, Calendar, Loader2, Check,
  ShoppingCart, Home as HomeIcon, HandHeart, Info,
} from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SeoHead from "@/components/SeoHead";
import ProductScannerModal from "@/components/ProductScannerModal";
import { PRODUCT_TAXONOMY, findNode } from "@/config/productTaxonomy";

const GREEN  = "#0F3D2E";
const AMBER  = "#E4A140";
const CREAM  = "#FBF7EF";
const MAROON = "#7B2A2E";

const PREFERENCES = [
  "Organic", "Halal", "Kosher", "Vegan", "Vegetarian", "Gluten-Free",
  "Dairy-Free", "Nut-Free", "Non-GMO", "Fair-Trade", "Local", "Wild-Caught",
];
const CHARACTERISTICS = [
  "Fresh", "Local", "Organic", "Wild-Caught", "Farm-Raised", "Imported",
  "Frozen", "Canned", "Dried", "Raw", "Cooked", "Artisan",
];
const BUYING_FOR = [
  { key: "self",     label: "Self" },
  { key: "friends",  label: "Friends" },
  { key: "family",   label: "Family" },
  { key: "neighbor", label: "Neighbor" },
  { key: "donation", label: "Donation" },
  { key: "resale",   label: "Resale" },
  { key: "other",    label: "Other — Specify" },
];
const USE_FOR = [
  "Cooking", "Baking", "Salads", "Steaming", "Frying", "Grilling",
  "Snack", "Meal Prep", "Gift", "Party",
];

/* ================================================================
   Main
   ================================================================ */
export default function AddWishProductForm() {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();

  const prefilled = loc.state?.product || null;
  const querySlug = params.get("slug");

  // ─ Product identity ────────────────────────────────────────
  const [slug, setSlug]           = useState(prefilled?.slug || querySlug || "");
  const [name, setName]           = useState(prefilled?.common_name || prefilled?.name || "");
  const [heroImage, setHeroImage] = useState(prefilled?.hero_image || prefilled?.image_url || null);
  const [category, setCategory]   = useState(prefilled?.category || "");
  const [barcode, setBarcode]     = useState(prefilled?.barcode || null);

  // ─ Taxonomy (three-tier) ────────────────────────────────────
  const [productTypeGroup, setProductTypeGroup]   = useState("");   // Food / Non-Food / Services
  const [productCategory, setProductCategory]     = useState("");   // Perishable / …
  const [productSubCategory, setProductSubCategory] = useState(""); // Fresh Fruits / …

  // ─ Product Characteristics ─────────────────────────────────
  const [characteristics, setCharacteristics] = useState([]);

  // ─ Images ──────────────────────────────────────────────────
  const [images, setImages]   = useState([]);        // string URLs
  const [uploading, setUploading] = useState(false);

  // ─ Other details ───────────────────────────────────────────
  const [vendorName, setVendorName] = useState("");
  const [preferences, setPreferences] = useState([]);
  const [buyingFor, setBuyingFor]   = useState([]);
  const [otherBuyingFor, setOtherBuyingFor] = useState("");
  const [useFor, setUseFor]         = useState([]);
  const [peopleServed, setPeopleServed] = useState("");
  const [purchaseQty, setPurchaseQty]   = useState("");
  const [unit, setUnit]                 = useState("each");
  const [targetPrice, setTargetPrice]   = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [isInStock, setIsInStock]       = useState(false);
  const [inStockQty, setInStockQty]     = useState("");
  const [notes, setNotes]               = useState("");

  // ─ Auto-mirror ─────────────────────────────────────────────
  const [addToShopping, setAddToShopping] = useState(true);
  const [addToPantry,   setAddToPantry]   = useState(true);
  const [addToDonation, setAddToDonation] = useState(false);

  // ─ Modals ──────────────────────────────────────────────────
  const [scannerOpen, setScannerOpen] = useState(false);
  const [typeSheetOpen, setTypeSheetOpen] = useState(false);
  const [buyingSheetOpen, setBuyingSheetOpen] = useState(false);

  const [saving, setSaving] = useState(false);

  // Hydrate from a slug URL param (opened from FoodItemPage etc.)
  useEffect(() => {
    if (!slug || prefilled) return;
    (async () => {
      try {
        const r = await http.get(`/food-items/${slug}`);
        const p = r.data;
        setName(p.common_name || p.name || slug);
        setHeroImage(p.hero_image || p.image_url || null);
        setCategory(p.category || "");
      } catch { /* ignore */ }
    })();
  }, [slug, prefilled]);

  // ─ Scanner match handler ───────────────────────────────────
  function onScanMatch(product /*, chars */) {
    if (!product) return;
    setSlug(product.slug || slug);
    setName(product.common_name || product.name || name);
    setHeroImage(product.hero_image || product.image_url || (product.images && product.images[0]) || heroImage);
    setCategory(product.category || category);
    if (product.barcode) setBarcode(product.barcode);
    if (product.product_type) setProductCategory(product.product_type);
    if (product.product_subtype) setProductSubCategory(product.product_subtype);
    toast.success("Prefilled from scan — fill in vendor / buying / notes below.");
    // Smooth-scroll to the "Other Details" section.
    setTimeout(() => document.getElementById("other-details")?.scrollIntoView({ behavior: "smooth" }), 250);
  }

  // ─ Image upload ────────────────────────────────────────────
  async function handleImagePick(fileList) {
    if (!fileList?.length) return;
    setUploading(true);
    const added = [];
    for (const f of Array.from(fileList)) {
      try {
        const form = new FormData();
        form.append("file", f);
        const r = await http.post("/lists/upload-image", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (r.data?.url) added.push(r.data.url);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Upload failed");
      }
    }
    if (added.length) setImages((p) => [...p, ...added]);
    setUploading(false);
  }
  function removeImage(url) {
    setImages((p) => p.filter((u) => u !== url));
  }

  // ─ Toggles helper ──────────────────────────────────────────
  const toggle = (arr, setArr) => (val) =>
    setArr((cur) => cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val]);
  const togglePref = toggle(preferences, setPreferences);
  const toggleChar = toggle(characteristics, setCharacteristics);
  const toggleUse  = toggle(useFor, setUseFor);

  // ─ Submit ──────────────────────────────────────────────────
  async function save() {
    if (!name.trim()) return toast.error("Add a product name (or scan a product).");
    if (!user) return toast.error("Please sign in first.");
    setSaving(true);
    try {
      const finalBuyingFor = buyingFor.map((b) => b === "other" && otherBuyingFor.trim() ? `Other: ${otherBuyingFor.trim()}` : b);
      const body = {
        food_item_slug:  slug || undefined,
        food_item_name:  name,
        food_item_image: heroImage || images[0] || undefined,
        category:        category || undefined,
        product_type:            productTypeGroup   || undefined,
        product_category:        productCategory    || undefined,
        product_sub_category:    productSubCategory || undefined,
        product_characteristics: characteristics.join(", ") || undefined,
        images:          images,
        vendor_name:     vendorName.trim() || undefined,
        preferences:     preferences,
        buying_for:      finalBuyingFor,
        use_for:         useFor,
        people_served:   peopleServed  ? Number(peopleServed)  : undefined,
        purchase_qty:    purchaseQty   ? Number(purchaseQty)   : undefined,
        unit:            unit || undefined,
        wish_price:      targetPrice   ? Number(targetPrice)   : undefined,
        purchase_target_date: purchaseDate || undefined,
        is_in_stock:     isInStock,
        in_stock_qty:    isInStock && inStockQty ? Number(inStockQty) : undefined,
        additional_notes: notes.trim() || undefined,
        add_to_shopping: addToShopping,
        add_to_pantry:   addToPantry,
        add_to_donation: addToDonation,
      };
      // We NEED a food_item_slug for the backend. If the user typed a
      // brand-new product name that isn't in our catalog, create a
      // user-prefixed anchor slug so we can't collide with an existing
      // catalog entry.
      if (!body.food_item_slug) {
        body.food_item_slug = "user-" + name.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 60);
      }
      await http.post("/lists/wishlist", body);
      toast.success("Added to your wishlist.");
      nav("/food-pulse/my-lists?tab=wishlist");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  const productTypeLabel = useMemo(() => {
    const bits = [productTypeGroup, productCategory, productSubCategory].filter(Boolean);
    return bits.length ? bits.join(" · ") : "Main category and subcategory";
  }, [productTypeGroup, productCategory, productSubCategory]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: CREAM, color: GREEN }}>
        <div className="max-w-md text-center">
          <h1 className="font-display text-3xl mb-2">Sign in first</h1>
          <p className="opacity-80 mb-6">You need an account to build your wishlist.</p>
          <Link to="/login" className="rounded-full px-6 py-3 font-semibold text-white" style={{ background: GREEN }}>
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-32" style={{ background: CREAM, color: GREEN }} data-testid="add-wish-form">
      <SeoHead section="food-pulse-wishlist" title="Add Wish Product — Food Pulse" description="Add something to your wishlist. We'll ping you when it's on offer." />

      {/* App-bar ------------------------------------------------ */}
      <div className="sticky top-0 z-30 backdrop-blur bg-emerald-900 text-white">
        <div className="max-w-2xl mx-auto flex items-center gap-3 px-4 py-3">
          <button onClick={() => nav(-1)} className="p-1.5 hover:bg-white/10 rounded-full" data-testid="wish-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="font-display text-lg">Add Wish Product</div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4 space-y-5">

        {/* Scan CTA ------------------------------------------ */}
        <button
          type="button"
          onClick={() => setScannerOpen(true)}
          className="w-full rounded-2xl px-4 py-4 font-semibold text-white flex items-center justify-center gap-2 shadow"
          style={{ background: `linear-gradient(90deg, ${GREEN} 0%, ${MAROON} 100%)` }}
          data-testid="wish-scan-btn"
        >
          <Camera className="w-5 h-5" /> Scan a product
        </button>

        {/* Prefilled product summary ------------------------ */}
        {(name || heroImage) && (
          <div className="rounded-2xl border p-3 flex items-center gap-3" style={{ borderColor: "#E9D8B4", background: "#fff" }}>
            {heroImage
              ? <img src={heroImage} alt="" className="w-14 h-14 object-cover rounded-xl" />
              : <div className="w-14 h-14 rounded-xl bg-black/5 flex items-center justify-center"><Package className="w-6 h-6 opacity-40" /></div>}
            <div className="flex-1 min-w-0">
              <input value={name} onChange={(e) => setName(e.target.value)}
                     placeholder="Product name"
                     className="w-full text-base font-semibold outline-none bg-transparent"
                     data-testid="wish-product-name" />
              {barcode && <div className="text-[11px] font-mono opacity-60">UPC {barcode}</div>}
              {category && <div className="text-xs opacity-70">{category}</div>}
            </div>
          </div>
        )}

        {/* Add Product Images -------------------------------- */}
        <Section title="Add Product Images" required>
          <div className="rounded-2xl border p-3 flex gap-3 flex-wrap items-center" style={{ borderColor: "#E9D8B4", background: "#fff" }}>
            {images.map((url, idx) => (
              <div key={url} className="relative">
                <img src={url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`}
                     alt="" className="w-20 h-20 object-cover rounded-xl" />
                <button onClick={() => removeImage(url)}
                        className="absolute -top-2 -right-2 rounded-full p-1 text-white"
                        style={{ background: "#DC2626" }}
                        data-testid={`wish-remove-img-${idx}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <label className="w-20 h-20 rounded-xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer hover:bg-amber-50"
                   style={{ borderColor: AMBER, color: GREEN }}
                   data-testid="wish-add-img">
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-6 h-6" />}
              <span className="text-[10px] mt-1">Add</span>
              <input type="file" accept="image/*" multiple className="hidden"
                     onChange={(e) => handleImagePick(e.target.files)} />
            </label>
          </div>
        </Section>

        {/* Product Type ------------------------------------ */}
        <Section title="Product Type">
          <button type="button" onClick={() => setTypeSheetOpen(true)}
                  className="w-full flex items-center justify-between rounded-2xl border px-3 py-3 text-left"
                  style={{ borderColor: "#E9D8B4", background: "#fff" }}
                  data-testid="wish-type-btn">
            <div className="flex items-center gap-2 min-w-0">
              <Layers className="w-4 h-4 opacity-70 flex-shrink-0" />
              <span className={`truncate text-sm ${productTypeLabel === "Main category and subcategory" ? "opacity-60" : "font-semibold"}`}>
                {productTypeLabel}
              </span>
            </div>
            <ChevronDown className="w-4 h-4 opacity-60 flex-shrink-0" />
          </button>
        </Section>

        {/* Product Characteristics ------------------------- */}
        <Section title="Product Characteristics">
          <ChipRow items={CHARACTERISTICS} selected={characteristics} onToggle={toggleChar} testid="wish-char" />
        </Section>

        {/* Other Details ----------------------------------- */}
        <div id="other-details" className="rounded-2xl border bg-white p-4 space-y-4" style={{ borderColor: "#E9D8B4" }}>
          <div className="font-semibold text-lg">Other Details</div>

          {/* Vendor ----------------------------- */}
          <Field label="Vendor Name">
            <div className="flex items-center gap-2">
              <div className="flex-1 flex items-center gap-2 rounded-xl border px-3 py-2" style={{ borderColor: "#E9D8B4" }}>
                <Store className="w-4 h-4 opacity-60" />
                <input value={vendorName} onChange={(e) => setVendorName(e.target.value)}
                       placeholder="Vendor Name" className="flex-1 outline-none bg-transparent text-sm"
                       data-testid="wish-vendor-name" />
              </div>
            </div>
          </Field>

          {/* Preferences ------------------------ */}
          <Field label="Product Preferences">
            <ChipRow items={PREFERENCES} selected={preferences} onToggle={togglePref} testid="wish-pref" />
          </Field>

          {/* Buying For ------------------------- */}
          <Field label="Product Buying For">
            <button type="button" onClick={() => setBuyingSheetOpen(true)}
                    className="w-full flex items-center justify-between rounded-xl border px-3 py-2 text-left"
                    style={{ borderColor: "#E9D8B4" }}
                    data-testid="wish-buyingfor-btn">
              <div className="flex items-center gap-2 min-w-0">
                <Users className="w-4 h-4 opacity-70 flex-shrink-0" />
                <span className={`truncate text-sm ${buyingFor.length ? "font-semibold" : "opacity-60"}`}>
                  {buyingFor.length ? buyingFor.join(", ") : "Who is this for?"}
                </span>
              </div>
              <ChevronDown className="w-4 h-4 opacity-60" />
            </button>
            {buyingFor.includes("other") && (
              <input value={otherBuyingFor} onChange={(e) => setOtherBuyingFor(e.target.value)}
                     placeholder="Specify" className="mt-2 w-full rounded-xl border px-3 py-2 text-sm outline-none"
                     style={{ borderColor: "#E9D8B4" }} />
            )}
          </Field>

          {/* Use For ---------------------------- */}
          <Field label="Use For">
            <ChipRow items={USE_FOR} selected={useFor} onToggle={toggleUse} testid="wish-use" />
          </Field>

          {/* Qty + people served + unit --------- */}
          <div className="grid grid-cols-3 gap-3">
            <Field label="Quantity">
              <input type="number" min="0" step="0.5" value={purchaseQty}
                     onChange={(e) => setPurchaseQty(e.target.value)}
                     className="w-full rounded-xl border px-3 py-2 outline-none text-sm"
                     style={{ borderColor: "#E9D8B4" }}
                     data-testid="wish-qty" />
            </Field>
            <Field label="Unit">
              <select value={unit} onChange={(e) => setUnit(e.target.value)}
                      className="w-full rounded-xl border px-3 py-2 outline-none text-sm bg-white"
                      style={{ borderColor: "#E9D8B4" }}>
                {["each","kg","g","lb","oz","litre","ml","dozen","pack","box"].map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </Field>
            <Field label="People served">
              <input type="number" min="1" value={peopleServed}
                     onChange={(e) => setPeopleServed(e.target.value)}
                     className="w-full rounded-xl border px-3 py-2 outline-none text-sm"
                     style={{ borderColor: "#E9D8B4" }}
                     data-testid="wish-people" />
            </Field>
          </div>

          {/* Target price + target date --------- */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Target price (each)">
              <input type="number" min="0" step="0.01" value={targetPrice}
                     onChange={(e) => setTargetPrice(e.target.value)}
                     placeholder="0.00"
                     className="w-full rounded-xl border px-3 py-2 outline-none text-sm"
                     style={{ borderColor: "#E9D8B4" }}
                     data-testid="wish-price" />
            </Field>
            <Field label="Buy by">
              <div className="flex items-center gap-2 rounded-xl border px-3 py-2" style={{ borderColor: "#E9D8B4" }}>
                <Calendar className="w-4 h-4 opacity-60" />
                <input type="date" value={purchaseDate}
                       onChange={(e) => setPurchaseDate(e.target.value)}
                       className="flex-1 outline-none bg-transparent text-sm"
                       data-testid="wish-date" />
              </div>
            </Field>
          </div>

          {/* In stock toggle -------------------- */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={isInStock}
                     onChange={(e) => setIsInStock(e.target.checked)}
                     className="w-4 h-4"
                     data-testid="wish-instock" />
              <span className="text-sm font-semibold">I already have some at home</span>
            </label>
            {isInStock && (
              <input type="number" min="0" step="0.5" value={inStockQty}
                     onChange={(e) => setInStockQty(e.target.value)}
                     placeholder={`Qty on hand (${unit})`}
                     className="w-full mt-2 rounded-xl border px-3 py-2 outline-none text-sm"
                     style={{ borderColor: "#E9D8B4" }}
                     data-testid="wish-instock-qty" />
            )}
          </div>

          {/* Notes ------------------------------ */}
          <Field label="Additional Notes">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                      rows={2} placeholder="Preferred brand, size, colour, story…"
                      className="w-full rounded-xl border px-3 py-2 outline-none text-sm"
                      style={{ borderColor: "#E9D8B4" }}
                      data-testid="wish-notes" />
          </Field>
        </div>

        {/* Auto-mirror toggles --------------------------- */}
        <Section title="Also add to">
          <div className="grid grid-cols-3 gap-2">
            <MirrorToggle icon={ShoppingCart} label="Shopping"  on={addToShopping} setOn={setAddToShopping} testid="wish-mirror-shop" />
            <MirrorToggle icon={HomeIcon}     label="Pantry"    on={addToPantry}   setOn={setAddToPantry}   testid="wish-mirror-pantry" />
            <MirrorToggle icon={HandHeart}    label="Donation"  on={addToDonation} setOn={setAddToDonation} testid="wish-mirror-donation" />
          </div>
          <p className="mt-2 text-xs opacity-60 flex items-start gap-1">
            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
            We auto-create sister rows so anything you add here shows up on the lists you flip on.
          </p>
        </Section>
      </div>

      {/* Sticky Save ---------------------------------------------- */}
      <div className="fixed bottom-0 left-0 right-0 border-t backdrop-blur bg-white/95 z-[55]" style={{ borderColor: "#E9D8B4" }}>
        <div className="max-w-2xl mx-auto px-4 py-3 flex gap-3">
          <button onClick={() => nav(-1)} className="rounded-full px-5 py-3 font-semibold border" style={{ borderColor: GREEN, color: GREEN }}
                  data-testid="wish-cancel">
            Cancel
          </button>
          <button onClick={save} disabled={saving}
                  className="flex-1 rounded-full px-5 py-3 font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{ background: GREEN }}
                  data-testid="wish-save">
            {saving
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
              : <><Check className="w-4 h-4" /> Add to Wishlist</>}
          </button>
        </div>
      </div>

      <ProductScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onMatch={onScanMatch}
      />
      <SelectProductTypeSheet
        open={typeSheetOpen}
        onClose={() => setTypeSheetOpen(false)}
        group={productTypeGroup} setGroup={setProductTypeGroup}
        category={productCategory} setCategory={setProductCategory}
        sub={productSubCategory} setSub={setProductSubCategory}
      />
      <BuyingForSheet
        open={buyingSheetOpen}
        onClose={() => setBuyingSheetOpen(false)}
        value={buyingFor} onChange={setBuyingFor}
      />
    </div>
  );
}

/* ================================================================
   Sub-components
   ================================================================ */
function Section({ title, required, children }) {
  return (
    <div>
      <div className="text-sm font-semibold mb-2">
        {title}{required && <span className="text-red-600 ml-0.5">*</span>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-widest opacity-70 mb-1">{label}</div>
      {children}
    </div>
  );
}

function ChipRow({ items, selected, onToggle, testid }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((v) => {
        const on = selected.includes(v);
        return (
          <button key={v} type="button" onClick={() => onToggle(v)}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition`}
                  style={{
                    background: on ? GREEN : "transparent",
                    color:      on ? "white" : GREEN,
                    borderColor: on ? GREEN : "#E9D8B4",
                  }}
                  data-testid={`${testid}-${v.toLowerCase().replace(/\s+/g, "-")}`}>
            {v}
          </button>
        );
      })}
    </div>
  );
}

function MirrorToggle({ icon: Ico, label, on, setOn, testid }) {
  return (
    <button type="button" onClick={() => setOn(!on)}
            className={`rounded-2xl border py-3 flex flex-col items-center gap-1 text-xs font-semibold transition`}
            style={{
              background: on ? GREEN : "white",
              color:      on ? "white" : GREEN,
              borderColor: on ? GREEN : "#E9D8B4",
            }}
            data-testid={testid}>
      <Ico className="w-5 h-5" />
      {label}
    </button>
  );
}

/* ---------- Select Product Type bottom sheet ---------- */
function SelectProductTypeSheet({ open, onClose, group, setGroup, category, setCategory, sub, setSub }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[90] bg-black/50 flex items-end justify-center" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="w-full sm:max-w-2xl bg-white rounded-t-3xl max-h-[80vh] overflow-hidden flex flex-col shadow-2xl"
           data-testid="type-sheet">
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "#EED9AC" }}>
          <div className="font-semibold">Select Product Type</div>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-black/5"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {PRODUCT_TAXONOMY.map((g) => (
            <div key={g.slug} className="mb-3">
              <button
                onClick={() => setGroup(g.slug === group ? "" : g.slug)}
                className={`w-full text-left font-semibold py-2 flex items-center justify-between`}
                data-testid={`type-group-${g.slug}`}
              >
                <span className={group === g.slug ? "text-emerald-800" : ""}>{g.label}</span>
                <ChevronDown className={`w-4 h-4 transition ${group === g.slug ? "rotate-180" : ""}`} />
              </button>
              {group === g.slug && (
                <div className="ml-4 border-l pl-3" style={{ borderColor: "#EED9AC" }}>
                  {(g.children || []).map((c) => (
                    <div key={c.slug} className="py-1">
                      <button
                        onClick={() => {
                          if (category === c.slug) { setCategory(""); setSub(""); }
                          else { setCategory(c.slug); setSub(""); }
                        }}
                        className={`text-left text-sm flex items-center gap-1 ${category === c.slug ? "font-semibold text-emerald-800" : "opacity-90"}`}
                        data-testid={`type-cat-${c.slug}`}
                      >
                        {c.children?.length ? (category === c.slug ? "▾" : "▸") : "•"} {c.label}
                      </button>
                      {category === c.slug && c.children?.length > 0 && (
                        <div className="ml-5 mt-1 flex flex-wrap gap-1">
                          {c.children.map((sc) => (
                            <button key={sc.slug}
                              onClick={() => setSub(sub === sc.slug ? "" : sc.slug)}
                              className={`rounded-full px-2 py-0.5 text-xs border`}
                              style={{
                                background: sub === sc.slug ? GREEN : "transparent",
                                color:      sub === sc.slug ? "white" : GREEN,
                                borderColor: sub === sc.slug ? GREEN : "#E9D8B4",
                              }}
                              data-testid={`type-sub-${sc.slug}`}
                            >
                              {sc.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="border-t p-3 flex gap-2" style={{ borderColor: "#EED9AC" }}>
          <button onClick={() => { setGroup(""); setCategory(""); setSub(""); }}
                  className="rounded-full px-4 py-2 text-sm font-semibold border" style={{ borderColor: GREEN, color: GREEN }}>
            Clear
          </button>
          <button onClick={onClose}
                  className="flex-1 rounded-full px-4 py-2 text-sm font-semibold text-white" style={{ background: GREEN }}
                  data-testid="type-sheet-done">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Buying For bottom sheet ---------- */
function BuyingForSheet({ open, onClose, value, onChange }) {
  if (!open) return null;
  const toggle = (k) => onChange(value.includes(k) ? value.filter((v) => v !== k) : [...value, k]);
  return (
    <div className="fixed inset-0 z-[90] bg-black/50 flex items-end justify-center" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="w-full sm:max-w-md bg-white rounded-t-3xl overflow-hidden shadow-2xl"
           data-testid="buyingfor-sheet">
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "#EED9AC" }}>
          <div className="font-semibold">Buying For</div>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-black/5"><X className="w-5 h-5" /></button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {BUYING_FOR.map((b) => {
            const on = value.includes(b.key);
            return (
              <button key={b.key} onClick={() => toggle(b.key)}
                      className={`w-full text-left px-5 py-4 border-b flex items-center justify-between`}
                      style={{ borderColor: "#EED9AC" }}
                      data-testid={`buyingfor-${b.key}`}>
                <span className={on ? "font-semibold text-emerald-800" : ""}>{b.label}</span>
                {on && <Check className="w-4 h-4 text-emerald-700" />}
              </button>
            );
          })}
        </div>
        <div className="p-3">
          <button onClick={onClose} className="w-full rounded-full px-4 py-2 text-sm font-semibold text-white" style={{ background: GREEN }}
                  data-testid="buyingfor-done">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
